import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChevronRight } from 'lucide-react';
import { apiService } from '@/lib/api';

const NAVY = '#13294B';
const BLUE = '#1E4ED8';
const SUB = '#5B6678';
const BORDER = '#E4E9F0';

const fmtFull = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtK = (n) => {
  n = Number(n || 0);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

function Readiness({ score }) {
  const s = Math.round(score || 0);
  const color = s >= 85 ? '#1F7A4F' : s >= 60 ? '#B45309' : '#B42318';
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="h-1.5 w-14 rounded-full bg-slate-200 overflow-hidden">
        <div className="h-full" style={{ width: `${s}%`, background: color }} />
      </div>
      <span className="text-xs font-semibold tabular-nums w-6 text-right" style={{ color }}>{s}</span>
    </div>
  );
}

function Switcher({ active }) {
  const link = (to, label, on) => (
    <a href={to} className="px-3 py-1 rounded-md text-[13px] font-semibold" style={on ? { background: NAVY, color: '#fff' } : { color: SUB }}>{label}</a>
  );
  return (
    <div className="w-full border-b bg-white" style={{ borderColor: BORDER }}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-11 flex items-center gap-2">
        <span className="text-[12px] uppercase tracking-wide text-slate-400 mr-2">Design preview</span>
        {link('/dashboard-option-a', 'Option A · Worklist', active === 'a')}
        {link('/dashboard-option-b', 'Option B · Focus', active === 'b')}
        {link('/dashboard', 'Current', false)}
      </div>
    </div>
  );
}

export default function DashboardOptionA({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await apiService.getDashboardOverview(user.id);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin" style={{ color: NAVY }} /></div>;
  }
  const d = data || {};
  const projects = (d.projects || []).filter((p) => !p.is_complete);
  const attention = projects
    .filter((p) => (p.started_phase_count ?? 0) > 0)
    .map((p) => ({ p, missing: (p.draw_items || []).filter((i) => !i.ok) }))
    .filter((x) => x.missing.length > 0)
    .sort((a, b) => (a.p.readiness_score ?? 0) - (b.p.readiness_score ?? 0));

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
      <Switcher active="a" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {/* Header — calm, no KPI chips */}
        <div className="flex items-end justify-between flex-wrap gap-4 pb-6 border-b" style={{ borderColor: BORDER }}>
          <div>
            <h1 className="text-3xl md:text-4xl" style={{ color: NAVY, fontFamily: "'Archivo', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' }}>Projects</h1>
            <p className="text-[15px] mt-2" style={{ color: SUB }}>
              {fmtFull(d.total_drawn)} drawn of {fmtFull(d.total_loan_principal)} · <span style={{ color: '#1F7A4F', fontWeight: 600 }}>{fmtFull(d.total_available_for_draw)} available to draw</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/reports')} className="text-[14px] font-semibold" style={{ color: SUB }}>Reports</button>
            <button onClick={() => navigate('/new-project-vision')} className="text-white text-sm font-semibold px-4 py-2.5 rounded-md" style={{ background: NAVY }}>New project</button>
          </div>
        </div>

        {/* Needs attention — terse worklist, no boxes */}
        {attention.length > 0 && (
          <section className="mt-8">
            <div className="text-[11px] uppercase tracking-wide font-semibold mb-2" style={{ color: '#98A2B3' }}>Needs attention</div>
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: BORDER }}>
              {attention.map(({ p, missing }) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/project/${p.id}?tab=loan`)}
                  className="w-full text-left flex items-center gap-4 px-4 py-3 border-b last:border-0 hover:bg-slate-50"
                  style={{ borderColor: BORDER }}
                >
                  <div className="min-w-0 flex-1">
                    <span className="text-[15px] font-semibold" style={{ color: NAVY }}>{p.name}</span>
                    <span className="text-[13px] ml-3" style={{ color: '#B42318' }}>
                      {missing.map((m) => m.label).slice(0, 4).join(' · ')}{missing.length > 4 ? ` +${missing.length - 4}` : ''}
                    </span>
                  </div>
                  <span className="text-[13px] font-semibold shrink-0 inline-flex items-center gap-1" style={{ color: BLUE }}>Prepare draw <ChevronRight className="w-4 h-4" /></span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Projects table — the centerpiece */}
        <section className="mt-8">
          <div className="text-[11px] uppercase tracking-wide font-semibold mb-2" style={{ color: '#98A2B3' }}>All active projects</div>
          <div className="border rounded-lg overflow-hidden" style={{ borderColor: BORDER }}>
            <table className="w-full">
              <thead>
                <tr className="text-left" style={{ background: '#F8FAFC' }}>
                  {['Project', 'Loan #', 'Drawn', 'Budget', 'Available', 'Readiness', ''].map((h, i) => (
                    <th key={h + i} className={`px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold ${i >= 2 && i <= 5 ? 'text-right' : ''}`} style={{ color: '#98A2B3' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm" style={{ color: SUB }}>No active projects yet.</td></tr>
                )}
                {projects.map((p) => {
                  const budget = p.total_budget || p.loan_principal_amount || 0;
                  const avail = Math.max(0, (p.loan_principal_amount || budget) - (p.amount_drawn || 0));
                  return (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/project/${p.id}`)}
                      className="border-t cursor-pointer hover:bg-slate-50"
                      style={{ borderColor: BORDER }}
                    >
                      <td className="px-4 py-3">
                        <div className="text-[15px] font-semibold truncate max-w-[260px]" style={{ color: NAVY }}>{p.name}</div>
                      </td>
                      <td className="px-4 py-3 text-[13px] tabular-nums" style={{ color: SUB }}>{p.loan_number || '—'}</td>
                      <td className="px-4 py-3 text-right text-[14px] tabular-nums" style={{ color: '#1D2939' }}>{p.drawn_pct}%<span className="block text-[11px]" style={{ color: '#98A2B3' }}>{fmtK(p.amount_drawn)}</span></td>
                      <td className="px-4 py-3 text-right text-[14px] tabular-nums" style={{ color: '#1D2939' }}>{fmtK(budget)}</td>
                      <td className="px-4 py-3 text-right text-[14px] tabular-nums" style={{ color: '#1F7A4F' }}>{fmtK(avail)}</td>
                      <td className="px-4 py-3"><Readiness score={p.readiness_score ?? 0} /></td>
                      <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 inline" style={{ color: '#98A2B3' }} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={() => navigate('/project-hub')} className="text-[13px] font-semibold mt-3 inline-flex items-center gap-1" style={{ color: BLUE }}>Open Project Hub <ChevronRight className="w-4 h-4" /></button>
        </section>
      </div>
    </div>
  );
}
